// PO register + create/edit (DRAFT only, per spec's "documents are freely
// editable in DRAFT, immutable once POSTED") + Send (DRAFT->OPEN) + Cancel.
// BUILD_SPEC.md §5.4, §8.1 step 1, §11. Plan.md G20.
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconPlus, IconShoppingCartOff, IconTrash } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { DownloadPdfLink } from '../sales-invoicing/components/DownloadPdfLink';
import { isCountLine, PRICING_BASES, VAT_TREATMENTS, type PurchaseOrderStatus } from '@zebra/core';
import { centsToRand, formatQtyDisplay, PRICING_BASIS_LABELS, randToCents } from './common';
import {
  type ProductRow,
  type PurchaseOrderRow,
  type PurchaseOrderWriteInput,
  useCancelPurchaseOrder,
  useCostCentres,
  useCreatePurchaseOrder,
  useLocations,
  usePurchaseOrder,
  usePurchaseOrders,
  useProducts,
  useSendPurchaseOrder,
  useSites,
  useSuppliers,
  useUpdatePurchaseOrder,
} from './api';

// A line's mode (mass tonnes+basis vs EACH count+per-unit price) follows the
// SELECTED PRODUCT's `baseUom` — not a field the operator picks directly —
// mirroring the server-side invariant (schemas/purchasing.ts's
// purchaseOrderLineInputSchema union, DB's `_quantity_mode` CHECK). `qtyTonnes`/
// `pricingBasis` and `qtyEach` both live on every `LineFormValue` regardless of
// current mode (only the active pair is ever read by `toInput`) so switching a
// line's product between an EACH and a mass product doesn't lose the other
// mode's half-entered values.
interface LineFormValue {
  productId: string;
  qtyTonnes: string;
  pricingBasis: 'FLAT_WET' | 'BDT' | 'MOISTURE_BAND';
  qtyEach: string;
  priceRand: string;
  vatTreatment: 'STANDARD' | 'ZERO_RATED' | 'EXEMPT';
  costCentreId: string;
}

interface PoFormValues {
  siteId: string;
  supplierId: string;
  expectedAt: string;
  deliveryLocationId: string;
  notes: string;
  lines: LineFormValue[];
}

function emptyLine(): LineFormValue {
  return { productId: '', qtyTonnes: '', pricingBasis: 'FLAT_WET', qtyEach: '', priceRand: '', vatTreatment: 'STANDARD', costCentreId: '' };
}

function isEachLine(line: LineFormValue, productsById: Map<string, ProductRow>): boolean {
  return productsById.get(line.productId)?.baseUom === 'EACH';
}

/** Builds the mode-correct wire variant per line — a count (EACH) line's
 * object literal carries ONLY `{qty, priceCentsPerUnit}` (never `qtyTonnes`/
 * `pricingBasis`/`bands`) and vice versa, matching the `.strict()` branches on
 * both sides of the server's union (a mixed-mode object would fail BOTH
 * branches, not silently drop the extra keys). */
function toInput(values: PoFormValues, productsById: Map<string, ProductRow>): PurchaseOrderWriteInput {
  return {
    siteId: values.siteId,
    supplierId: values.supplierId,
    expectedAt: values.expectedAt ? new Date(values.expectedAt) : null,
    deliveryLocationId: values.deliveryLocationId || null,
    notes: values.notes.trim() || null,
    currency: 'ZAR',
    lines: values.lines.map((line, index) => {
      const lineNo = index + 1;
      if (isEachLine(line, productsById)) {
        return {
          lineNo,
          productId: line.productId,
          qty: line.qtyEach,
          priceCentsPerUnit: randToCents(line.priceRand),
          vatTreatment: line.vatTreatment,
          costCentreId: line.costCentreId || null,
        };
      }
      return {
        lineNo,
        productId: line.productId,
        qtyTonnes: line.qtyTonnes,
        pricingBasis: line.pricingBasis,
        priceCentsPerTonne: line.pricingBasis === 'MOISTURE_BAND' ? null : randToCents(line.priceRand),
        bands: null,
        vatTreatment: line.vatTreatment,
        costCentreId: line.costCentreId || null,
      };
    }),
  };
}

function PurchaseOrderFormModal({
  opened,
  onClose,
  editingId,
}: {
  opened: boolean;
  onClose: () => void;
  editingId: string | null;
}) {
  const { data: sites } = useSites();
  const { data: suppliers } = useSuppliers({ isActive: true });
  const { data: products } = useProducts({ isActive: true });
  const { data: costCentres } = useCostCentres();
  const { data: detail } = usePurchaseOrder(editingId ?? undefined);
  const { data: locations } = useLocations();
  const canOperate = useCanOperate();

  const create = useCreatePurchaseOrder();
  const update = useUpdatePurchaseOrder(editingId ?? '');
  const mutation = editingId ? update : create;
  const readOnly = Boolean(detail && detail.status !== 'DRAFT');

  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);

  const form = useForm<PoFormValues>({
    initialValues: {
      siteId: '',
      supplierId: '',
      expectedAt: '',
      deliveryLocationId: '',
      notes: '',
      lines: [emptyLine()],
    },
    // Function form (not the per-field object form) so a dynamic list item
    // (`lines.${i}.qtyEach`) can be validated per-mode — an EACH line's
    // quantity must be a whole positive number (client-side echo of
    // schemas/purchasing.ts's qtyEachSchema; the server remains the actual
    // enforcement point).
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.siteId) errors.siteId = 'Site is required';
      if (!values.supplierId) errors.supplierId = 'Supplier is required';
      if (values.lines.length === 0) errors.lines = 'At least one line is required';
      values.lines.forEach((line, i) => {
        if (!isEachLine(line, productsById)) return;
        const n = Number(line.qtyEach);
        if (!line.qtyEach.trim() || Number.isNaN(n) || n <= 0) {
          errors[`lines.${i}.qtyEach`] = 'Quantity is required';
        } else if (!Number.isInteger(n)) {
          errors[`lines.${i}.qtyEach`] = 'Must be a whole number of units';
        }
      });
      return errors;
    },
  });

  // Populate the form once detail loads for an existing DRAFT. The parent
  // (PurchaseOrderRegister) remounts this modal per-open via `key={editingId ??
  // 'new'}`, so this effect runs exactly once per editing session.
  useEffect(() => {
    if (!detail || readOnly) return;
    form.setValues({
      siteId: detail.siteId,
      supplierId: detail.supplierId,
      expectedAt: detail.expectedAt ? detail.expectedAt.slice(0, 10) : '',
      deliveryLocationId: detail.deliveryLocationId ?? '',
      notes: detail.notes ?? '',
      lines: detail.lines.map((l) => ({
        productId: l.productId,
        qtyTonnes: l.qtyTonnes ?? '',
        pricingBasis: l.pricingBasis ?? 'FLAT_WET',
        qtyEach: l.qty ?? '',
        priceRand: isCountLine(l) ? centsToRand(l.priceCentsPerUnit) : centsToRand(l.priceCentsPerTonne),
        vatTreatment: l.vatTreatment,
        costCentreId: l.costCentreId ?? '',
      })),
    });
  }, [detail, readOnly]);

  function handleSubmit(values: PoFormValues) {
    mutation.mutate(toInput(values, productsById), { onSuccess: () => onClose() });
  }

  const supplierOptions = (suppliers?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));
  const siteOptions = (sites?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));
  const productOptions = (products?.items ?? []).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }));
  const selectedSiteId = form.values.siteId;
  const locationOptions = (locations?.items ?? [])
    .filter((l) => !selectedSiteId || l.siteId === selectedSiteId)
    .map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }));
  const costCentreOptions = (costCentres?.items ?? []).map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }));

  if (readOnly && detail) {
    return (
      <Modal opened={opened} onClose={onClose} title={`Purchase order ${detail.number}`} centered size="lg">
        <Stack gap="sm">
          <Group justify="space-between">
            <DocStatusBadge status={detail.status} domain="purchaseOrder" />
            {/* Regenerated server-side on every download (PO stays editable through
                DRAFT/OPEN/PARTIAL — see services/api's purchase-orders.ts `/:id/pdf`
                doc comment), so it's always current; unavailable once CANCELLED. */}
            <DownloadPdfLink
              label="Download PDF"
              pdfKey={detail.status !== 'CANCELLED' ? detail.id : null}
              downloadPath={`/purchasing/purchase-orders/${detail.id}/pdf`}
            />
          </Group>
          <Text size="sm" c="dimmed">
            Purchase orders can only be edited while DRAFT. Use Cancel from the register if this order needs to change.
          </Text>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Qty</Table.Th>
                  <Table.Th>Basis / price</Table.Th>
                  <Table.Th>Received</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detail.lines.map((line) => {
                  const each = isCountLine(line);
                  return (
                    <Table.Tr key={line.id}>
                      <Table.Td>{productOptions.find((p) => p.value === line.productId)?.label ?? line.productId}</Table.Td>
                      <Table.Td>{each ? `${formatQtyDisplay(line.qty, 0)} units` : `${formatQtyDisplay(line.qtyTonnes)} t`}</Table.Td>
                      <Table.Td>
                        {each ? `R${centsToRand(line.priceCentsPerUnit)} / unit` : PRICING_BASIS_LABELS[line.pricingBasis ?? 'FLAT_WET']}
                      </Table.Td>
                      <Table.Td>{each ? `${formatQtyDisplay(line.receivedQty, 0)} units` : `${formatQtyDisplay(line.receivedQtyKg)} kg`}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group justify="flex-end">
            <Button onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editingId ? 'Edit purchase order (DRAFT)' : 'New purchase order'} centered size="xl">
      {editingId && (
        // Outside the <form> below deliberately — Mantine's Button defaults its
        // native `type` to "submit" inside a <form>, and DownloadPdfLink (imported,
        // not editable by this goal) doesn't expose a `type` override; nesting it
        // inside the edit form would submit the PO update whenever a user just
        // wanted to download the PDF. A DRAFT PO's PDF is watermarked server-side
        // (pdf/purchase-order.ts) and regenerated on every download, so this is safe
        // to show even mid-edit.
        <Group justify="flex-end" mb="xs">
          <DownloadPdfLink label="Download PDF" pdfKey={editingId} downloadPath={`/purchasing/purchase-orders/${editingId}/pdf`} />
        </Group>
      )}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save purchase order'}
            </Alert>
          )}
          <Group grow>
            <Select label="Site" data={siteOptions} required searchable {...form.getInputProps('siteId')} />
            <Select label="Supplier" data={supplierOptions} required searchable {...form.getInputProps('supplierId')} />
          </Group>
          <Group grow>
            <TextInput label="Expected date (optional)" type="date" {...form.getInputProps('expectedAt')} />
            <Select label="Delivery location (optional)" data={locationOptions} clearable {...form.getInputProps('deliveryLocationId')} />
          </Group>
          <Textarea label="Notes" autosize minRows={1} {...form.getInputProps('notes')} />

          <Text fw={500} size="sm">
            Lines
          </Text>
          <Stack gap="xs">
            {form.values.lines.map((line, index) => {
              const each = isEachLine(line, productsById);
              return (
              <Paper key={index} withBorder p="sm" radius="md">
                <Stack gap="xs">
                  <Group grow>
                    <Select
                      label="Product"
                      data={productOptions}
                      searchable
                      required
                      {...form.getInputProps(`lines.${index}.productId`)}
                    />
                    {each ? (
                      <TextInput label="Quantity (units)" required {...form.getInputProps(`lines.${index}.qtyEach`)} />
                    ) : (
                      <TextInput label="Qty (tonnes)" required {...form.getInputProps(`lines.${index}.qtyTonnes`)} />
                    )}
                  </Group>
                  {each ? (
                    <Group grow>
                      <TextInput label="Price per unit (ZAR)" required {...form.getInputProps(`lines.${index}.priceRand`)} />
                      <Select
                        label="VAT treatment"
                        data={VAT_TREATMENTS.map((v) => ({ value: v, label: v }))}
                        required
                        {...form.getInputProps(`lines.${index}.vatTreatment`)}
                      />
                    </Group>
                  ) : (
                    <Group grow>
                      <Select
                        label="Pricing basis"
                        data={PRICING_BASES.map((b) => ({ value: b, label: PRICING_BASIS_LABELS[b] }))}
                        required
                        {...form.getInputProps(`lines.${index}.pricingBasis`)}
                      />
                      {line.pricingBasis !== 'MOISTURE_BAND' ? (
                        <TextInput
                          label="Price per tonne (ZAR)"
                          required
                          {...form.getInputProps(`lines.${index}.priceRand`)}
                        />
                      ) : (
                        <Text size="xs" c="dimmed" mt="xl">
                          Moisture-band pricing on a PO line is set up as a supplier contract; link this line's product to
                          an active MOISTURE_BAND contract on the Contracts tab.
                        </Text>
                      )}
                      <Select
                        label="VAT treatment"
                        data={VAT_TREATMENTS.map((v) => ({ value: v, label: v }))}
                        required
                        {...form.getInputProps(`lines.${index}.vatTreatment`)}
                      />
                    </Group>
                  )}
                  <Group grow align="flex-end">
                    <Select label="Cost centre (optional)" data={costCentreOptions} clearable {...form.getInputProps(`lines.${index}.costCentreId`)} />
                    <Button
                      color="red"
                      variant="subtle"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => form.removeListItem('lines', index)}
                      disabled={form.values.lines.length <= 1}
                    >
                      Remove line
                    </Button>
                  </Group>
                </Stack>
              </Paper>
              );
            })}
          </Stack>
          <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => form.insertListItem('lines', emptyLine())}>
            Add line
          </Button>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button type="submit" disabled={!canOperate} loading={mutation.isPending}>
                  {editingId ? 'Save changes' : 'Create draft PO'}
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function RowActions({ row }: { row: PurchaseOrderRow }) {
  const canOperate = useCanOperate();
  const send = useSendPurchaseOrder(row.id);
  const cancel = useCancelPurchaseOrder(row.id);
  return (
    <Group gap="xs" wrap="nowrap">
      {row.status === 'DRAFT' && (
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <Button size="xs" disabled={!canOperate} onClick={() => send.mutate()} loading={send.isPending}>
              Send
            </Button>
          </span>
        </Tooltip>
      )}
      {(row.status === 'DRAFT' || row.status === 'OPEN' || row.status === 'PARTIAL') && (
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <ConfirmButton
              size="xs"
              color="red"
              variant="light"
              disabled={!canOperate}
              loading={cancel.isPending}
              label="Cancel"
              message={`Cancel PO ${row.number}? It will be marked CANCELLED and can no longer be sent or received against — this can't be undone from here.`}
              onConfirm={() => cancel.mutate()}
            />
          </span>
        </Tooltip>
      )}
    </Group>
  );
}

export function PurchaseOrderRegister() {
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | null>(null);
  const { data, isLoading, isError } = usePurchaseOrders({ status: statusFilter ?? undefined });
  const { data: suppliers } = useSuppliers();
  const [modalOpened, setModalOpened] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const supplierName = useMemo(() => new Map((suppliers?.items ?? []).map((s) => [s.id, s.name])), [suppliers]);
  const rows = data?.items ?? [];
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26): on an empty
  // register the EmptyState below already offers "New PO" as the sole
  // call-to-action — showing the same button in the page header too rendered
  // TWO "New PO" buttons on a fresh tenant (Playwright strict-mode failure).
  // Reuses the exact emptiness condition the EmptyState branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  function openCreate() {
    setEditingId(null);
    setModalOpened(true);
  }
  function openRow(row: PurchaseOrderRow) {
    setEditingId(row.id);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Purchase orders"
        description="Raise and track purchase orders with your suppliers, from order through to delivery."
        actions={
          <Group gap="sm">
            <Select
              placeholder="All statuses"
              clearable
              data={['DRAFT', 'OPEN', 'PARTIAL', 'COMPLETE', 'CANCELLED']}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as PurchaseOrderStatus | null)}
              w={160}
            />
            {showHeaderCreate && (
              <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                New PO
              </Button>
            )}
          </Group>
        }
      />

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load purchase orders.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>Supplier</Table.Th>
                <Table.Th>Expected</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => openRow(row)}>
                      {row.number}
                    </Button>
                  </Table.Td>
                  <Table.Td>{supplierName.get(row.supplierId) ?? row.supplierId}</Table.Td>
                  <Table.Td>{row.expectedAt ? new Date(row.expectedAt).toLocaleDateString() : '—'}</Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={row.status} domain="purchaseOrder" />
                  </Table.Td>
                  <Table.Td>
                    <RowActions row={row} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <EmptyState
                      icon={IconShoppingCartOff}
                      title="No purchase orders yet"
                      hint="Raise a purchase order to start tracking what you've ordered from a supplier."
                      action={
                        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
                          New PO
                        </Button>
                      }
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {modalOpened && (
        <PurchaseOrderFormModal key={editingId ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editingId={editingId} />
      )}
    </Stack>
  );
}
